// ─────────────────────────────────────────────
//  useDirectiveReply — answering a directive, once, for every surface.
//
//  A recipient can say two things about an instruction: "Got it" and "I can't,
//  because…". Both are a write plus a rollback, and both have a rule that is
//  easy to get subtly wrong in a second copy:
//
//    • A receipt the board never received, displayed as "seen", is the single
//      failure this whole feature exists to prevent. So a rejected write must
//      roll the row BACK to unanswered and say so.
//    • A refusal the board never received, displayed as "you said no", is the
//      same lie pointed the other way: the provider stops expecting anybody and
//      the board goes on expecting them.
//
//  ⚠️ WHY A HOOK AND NOT A SECOND COPY. The phone already had this logic; the
//  web had no way to answer a directive AT ALL, so a CRNA on the web app could
//  be sent to cover a break and could neither accept nor refuse — while the new
//  unanswered-directive escalation nagged the board about them forever. Writing
//  the second surface by copying the first is exactly how BreakEnginePanel and
//  VoiceControl drifted from the break card and made "synced everywhere"
//  untrue. One implementation, two renderers — the same shape `breakBoardRows`
//  already uses for the BREAKS card and the phone break board.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import { useCallback, useState } from "react";
import { confirmTask, declineTask } from "../firebase/firestore";

export default function useDirectiveReply(uid) {
  const [busyId, setBusyId] = useState(null);
  const [failedIds, setFailedIds] = useState({});
  // ⚠️ THE TAP THIS DEVICE JUST MADE. `serverTimestamp()` is NULL on the local
  // echo of the write, so for the whole round trip — forever, on a phone that
  // has lost signal in a stairwell — the document still says this user has not
  // confirmed. Without the local flag the button sits there unchanged after a
  // tap and the provider presses it again and again.
  const [seenLocally, setSeenLocally] = useState({});

  const clearFailure = useCallback((id) => {
    setFailedIds((prev) => (prev[id] ? { ...prev, [id]: false } : prev));
  }, []);

  const confirm = useCallback(
    async (task) => {
      if (!uid || !task?.id || busyId) return false;
      setBusyId(task.id);
      setSeenLocally((prev) => ({ ...prev, [task.id]: true }));
      clearFailure(task.id);
      try {
        await confirmTask(task.id, uid);
        return true;
      } catch (err) {
        console.error("Task confirm failed:", err);
        setSeenLocally((prev) => ({ ...prev, [task.id]: false }));
        setFailedIds((prev) => ({ ...prev, [task.id]: true }));
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [uid, busyId, clearFailure]
  );

  const decline = useCallback(
    async (task, reason) => {
      if (!uid || !task?.id || busyId) return false;
      setBusyId(task.id);
      clearFailure(task.id);
      try {
        await declineTask(task.id, uid, reason);
        return true;
      } catch (err) {
        console.error("Task decline failed:", err);
        setFailedIds((prev) => ({ ...prev, [task.id]: true }));
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [uid, busyId, clearFailure]
  );

  return { busyId, failedIds, seenLocally, confirm, decline };
}
